import { Alert, Button, Space, Typography } from "antd";
import { useEffect, useRef, useState } from "react";
import { Dialog as ZhDialog, ZhMap, type ShipInfoVo, type ZhMapRef } from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import type { ShipDeviceOption } from "./ShipDeviceBindingModal";

export interface ShipMonitorDevice extends ShipDeviceOption {
    serialNo?: string;
}

export interface ShipMonitorStream {
    playUrl?: string;
    liveId?: string;
}

export type ShipMonitorMapShip = Pick<ShipInfoVo, "id" | "lon" | "lat"> &
    Partial<Pick<ShipInfoVo, "name" | "cnname">> & {
        location?: string;
    };

export interface ShipMonitorModalProps {
    open: boolean;
    device?: ShipMonitorDevice;
    loadMonitorStream?: (device: ShipMonitorDevice) => Promise<ShipMonitorStream | undefined>;
    loadPlaybackStream?: (device: ShipMonitorDevice, playbackAt?: string) => Promise<ShipMonitorStream | undefined>;
    stopMonitorStream?: (serialNo: string, liveId: string) => Promise<unknown>;
    stopPlaybackStream?: (serialNo: string, liveId: string) => Promise<unknown>;
    shipId?: string;
    ship?: Partial<ShipMonitorMapShip>;
    loadShipLocation?: (mmsi: string) => Promise<ShipMonitorMapShip | undefined>;
    onCancel: () => void;
}

/** 复用 Vue videoDialog 的直播取流接口。 */
export async function loadShipMonitorStream(device: ShipMonitorDevice) {
    if (!device.serialNo) throw new Error("设备缺少序列号");
    const response = await shippingRequestHttp.service.get<{ playUrl?: string; liveId?: string }>(
        "/device/media/video",
        { params: { deviceSerial: device.serialNo }, timeout: 30000 }
    );
    return response.data;
}

/** 复用 Vue videoDialog 的船舶位置接口，为 zhihao 地图提供当前船舶点位。 */
export async function loadShipMonitorLocation(mmsi: string): Promise<ShipMonitorMapShip | undefined> {
    const response = await shippingRequestHttp.service.get<ShipMonitorMapShip>("/ship/location", { params: { mmsi } });
    return response.data;
}

function dateKey(date: Date) {
    const pad = (value: number) => String(value).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** 复用 Vue playback/timeLine 的回放取流参数，默认覆盖当天 24 小时。 */
export async function loadShipPlaybackStream(device: ShipMonitorDevice, playbackAt?: string) {
    if (!device.serialNo) throw new Error("设备缺少序列号");
    const selected = playbackAt ? new Date(playbackAt.replace(" ", "T")) : new Date();
    const date = Number.isNaN(selected.getTime()) ? new Date() : selected;
    const pad = (value: number) => String(value).padStart(2, "0");
    const selectedTime = playbackAt
        ? `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
        : "T00:00:00";
    const response = await shippingRequestHttp.service.get<{ playUrl?: string; liveId?: string }>(
        "/device/media/video",
        {
            params: {
                deviceSerial: device.serialNo,
                type: 2,
                startTime: `${dateKey(date)}${selectedTime}`,
                endTime: `${dateKey(date)}T23:59:59`
            },
            timeout: 30000
        }
    );
    return response.data;
}

/** 复用 Vue deviceService.stopPlay 的直播停止参数。 */
export async function stopShipMonitorStream(serialNo: string, liveId: string) {
    const response = await shippingRequestHttp.service.put(`/device/stop/${serialNo}?type=PLAY&liveId=${liveId}`);
    return response.data;
}

/** 复用 Vue deviceService.stopPlay 的回放停止参数。 */
export async function stopShipPlaybackStream(serialNo: string, liveId: string) {
    const response = await shippingRequestHttp.service.put(`/device/stop/${serialNo}?type=PLAYBACK&liveId=${liveId}`);
    return response.data;
}

/** 船舶监控弹窗，提供直播/回放切换并在窗口生命周期结束时停止当前流。 */
export function ShipMonitorModal({
    open,
    device,
    loadMonitorStream = loadShipMonitorStream,
    loadPlaybackStream = loadShipPlaybackStream,
    stopMonitorStream = stopShipMonitorStream,
    stopPlaybackStream = stopShipPlaybackStream,
    shipId,
    ship,
    loadShipLocation = loadShipMonitorLocation,
    onCancel
}: ShipMonitorModalProps) {
    const [mode, setMode] = useState<"live" | "playback">("live");
    const [stream, setStream] = useState<ShipMonitorStream>();
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [playbackPosition, setPlaybackPosition] = useState(0);
    const [mapShip, setMapShip] = useState<ShipInfoVo>();
    const [shipLocation, setShipLocation] = useState("");
    const mapRef = useRef<ZhMapRef | null>(null);

    /** 打开窗口后自动加载直播，保持 Vue videoDialog 首屏行为。 */
    useEffect(() => {
        if (!open || !device) return;
        setMode("live");
        setStream(undefined);
        setErrorMessage("");
        setPlaybackPosition(0);
        setLoading(true);
        void loadMonitorStream(device)
            .then(result => setStream(result))
            .catch(() => setErrorMessage("监控加载失败"))
            .finally(() => setLoading(false));
    }, [device, loadMonitorStream, open]);

    /** 打开窗口后加载船舶位置，保持 Vue 地图定位与底部位置文案契约。 */
    useEffect(() => {
        if (!open || !shipId) return;
        let disposed = false;
        void loadShipLocation(shipId)
            .then(result => {
                if (disposed) return;
                const candidate = result || ship;
                const lon = Number(candidate?.lon);
                const lat = Number(candidate?.lat);
                if (candidate?.id != null && Number.isFinite(lon) && Number.isFinite(lat)) {
                    setMapShip({
                        id: String(candidate.id),
                        lon,
                        lat,
                        name: candidate.name,
                        cnname: candidate.cnname
                    });
                }
                setShipLocation(candidate?.location || "");
            })
            .catch(() => {
                if (disposed || !ship) return;
                const lon = Number(ship.lon);
                const lat = Number(ship.lat);
                if (ship.id != null && Number.isFinite(lon) && Number.isFinite(lat)) {
                    setMapShip({ id: String(ship.id), lon, lat, name: ship.name, cnname: ship.cnname });
                }
                setShipLocation(ship.location || "");
            });
        return () => {
            disposed = true;
        };
    }, [loadShipLocation, open, ship, shipId]);

    /** 将定位点绘制、选中并居中到 zhihao-ui-react 地图。 */
    useEffect(() => {
        if (!mapShip) return;
        mapRef.current?.ship.render([mapShip]);
        mapRef.current?.ship.selected(mapShip);
        mapRef.current?.setCenter(mapShip.lon, mapShip.lat);
    }, [mapShip]);

    /** 切换直播和回放并重新取流，回放沿用当天时间范围。 */
    const handleModeSwitch = async () => {
        if (!device || loading) return;
        const nextMode = mode === "live" ? "playback" : "live";
        setLoading(true);
        setErrorMessage("");
        try {
            const result =
                nextMode === "playback" ? await loadPlaybackStream(device, undefined) : await loadMonitorStream(device);
            setMode(nextMode);
            setStream(result);
            setPlaybackPosition(0);
        } catch {
            setErrorMessage("监控加载失败");
        } finally {
            setLoading(false);
        }
    };

    /** 回放时间轴变更时重新取流，保留 Vue timeLine 的秒级定位语义。 */
    const handleTimelineChange = async (value: number) => {
        setPlaybackPosition(value);
        if (!device || mode !== "playback" || loading) return;
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        date.setSeconds(value);
        setLoading(true);
        try {
            const result = await loadPlaybackStream(
                device,
                `${dateKey(date)} ${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}:${String(date.getSeconds()).padStart(2, "0")}`
            );
            setStream(result);
            setErrorMessage("");
        } catch {
            setErrorMessage("监控加载失败");
        } finally {
            setLoading(false);
        }
    };

    /** 关闭时停止当前流，避免直播/回放连接泄漏。 */
    useEffect(() => {
        if (!open || !device?.serialNo || !stream?.liveId) return;
        return () => {
            const stop = mode === "playback" ? stopPlaybackStream : stopMonitorStream;
            void stop(device.serialNo!, stream.liveId!).catch(() => undefined);
        };
    }, [device?.serialNo, mode, open, stopMonitorStream, stopPlaybackStream, stream]);

    return (
        <ZhDialog
            open={open}
            title="监控调阅"
            width="842px"
            onCancel={onCancel}
            closeOnClickModal={false}
            closeOnPressEscape={false}
            destroyOnHidden
            footer={
                <Space>
                    <Button loading={loading} onClick={() => void handleModeSwitch()}>
                        {mode === "live" ? "视频回放" : "直播"}
                    </Button>
                    <Button onClick={onCancel}>取消</Button>
                </Space>
            }
        >
            {errorMessage ? <Alert title={errorMessage} role="alert" type="error" showIcon /> : null}
            <Typography.Paragraph>监控设备：{device?.serialNo || "--"}</Typography.Paragraph>
            {stream?.liveId ? <Typography.Paragraph>流 ID：{stream.liveId}</Typography.Paragraph> : null}
            <div aria-label="船舶位置地图" className="mb-4 h-56 w-full" data-testid="ship-monitor-map">
                <ZhMap ref={mapRef} className="h-full w-full" layerType="satellite" showScale />
            </div>
            {shipLocation ? (
                <Typography.Paragraph aria-label="船舶当前位置">当前位置：{shipLocation}</Typography.Paragraph>
            ) : null}
            {mode === "playback" ? (
                <Space orientation="vertical" className="w-full">
                    <label htmlFor="ship-playback-timeline">回放时间轴</label>
                    <input
                        id="ship-playback-timeline"
                        aria-label="回放时间轴"
                        className="w-full"
                        type="range"
                        min={0}
                        max={86399}
                        value={playbackPosition}
                        disabled={loading}
                        onChange={event => void handleTimelineChange(Number(event.target.value))}
                    />
                </Space>
            ) : null}
            <video data-testid="ship-monitor-video" className="h-64 w-full" controls src={stream?.playUrl} />
        </ZhDialog>
    );
}

export default ShipMonitorModal;
